import React from "react";

/**
 * The card primitive the Modern view is built from. The design repeats this
 * header strip about a dozen times — title, optional count chip, muted hint,
 * spacer, right-aligned actions — so it lives here once.
 *
 * `clip={false}` is for a panel whose header actions open an absolute
 * dropdown (the production InvoiceActionsDropdown in CustomsPanel). The root's
 * overflow-hidden cut that menu off at the panel edge, so documents and their
 * trash icons below the fold could not be reached. With clip off, only the
 * body is clipped to the rounded corners and the header menu can overhang
 * (downwards into the scrolling invoice body, so every row stays reachable).
 */
export const Panel = ({ title, chip, chipTone = "neutral", hint, actions, children, className = "", clip = true }) => (
  <div
    className={`min-w-0 ${clip ? "overflow-hidden" : ""} rounded-[7px] border border-ui-border bg-ui-surface ${className}`}
  >
    <div
      className={`flex flex-wrap items-center gap-1.5 border-b border-ui-border bg-ui-surface-sunken px-2.5 py-1.5 ${
        clip ? "" : "rounded-t-[6px]"
      }`}
    >
      <span className="font-jakarta text-[12px] font-bold text-ui-text">
        {title}
      </span>
      {chip != null && <Chip tone={chipTone}>{chip}</Chip>}
      {hint && (
        <span className="font-manrope text-[11.5px] font-medium text-ui-text-muted">
          {hint}
        </span>
      )}
      {actions && (
        <>
          <div className="flex-1" />
          {/* ml-auto keeps a wrapped action at the right edge, so a menu
              that opens leftwards from it stays inside the panel. */}
          {clip ? actions : <div className="ml-auto">{actions}</div>}
        </>
      )}
    </div>
    {clip ? (
      children
    ) : (
      <div className="overflow-hidden rounded-b-[6px]">{children}</div>
    )}
  </div>
);

const CHIP_TONES = {
  neutral:
    "border-ui-border bg-state-neutral-bg text-state-neutral-text",
  info: "border-state-info-border bg-state-info-bg text-state-info-text",
  success:
    "border-state-success-border bg-state-success-bg text-state-success-text",
  warning:
    "border-state-warning-border bg-state-warning-bg text-state-warning-text",
  danger:
    "border-state-danger-border bg-state-danger-bg text-state-danger-text",
  purple:
    "border-state-purple-border bg-state-purple-bg text-state-purple-text",
  sky: "border-state-sky-border bg-state-sky-bg text-state-sky-text",
};

export const Chip = ({ tone = "neutral", children }) => (
  <span
    className={`rounded border px-[7px] py-0.5 font-manrope text-[10.5px] font-semibold ${
      CHIP_TONES[tone] ?? CHIP_TONES.neutral
    }`}
  >
    {children}
  </span>
);

/** Uppercase micro-label. The design uses this for every field eyebrow. */
export const Eyebrow = ({ children, size = "md", className = "" }) => (
  <div
    className={`font-jakarta font-semibold uppercase text-ui-text-muted ${
      size === "sm"
        ? "text-[8.5px] tracking-[0.09em]"
        : "text-[10px] tracking-[0.07em]"
    } ${className}`}
  >
    {children}
  </div>
);

const BADGE_TONES = {
  neutral: "border-ui-border bg-state-neutral-bg text-ui-text-muted",
  purple:
    "border-state-purple-border bg-state-purple-bg text-state-purple-text",
  info: "border-state-info-border bg-state-info-bg text-state-info-text",
  warning:
    "border-state-warning-border bg-state-warning-bg text-state-warning-text",
  success:
    "border-state-success-border bg-state-success-bg text-state-success-text",
};

/**
 * The tiny inline tag the design puts next to a label — DERIVED, MARKUP,
 * ON INVOICE. Smaller and tighter than a Chip: it rides inside a line of text
 * rather than sitting beside a title, so it must not change the line's height.
 */
export const MicroBadge = ({ tone = "neutral", children, className = "" }) => (
  <span
    className={`ml-1.5 inline-block whitespace-nowrap rounded-[3px] border px-1 align-[1px] font-manrope text-[7.5px] font-bold uppercase leading-[1.7] tracking-[0.04em] ${
      BADGE_TONES[tone] ?? BADGE_TONES.neutral
    } ${className}`}
  >
    {children}
  </span>
);

/**
 * Body copy with a bolded lead-in — the design's way of making a sentence
 * scannable without a separate heading. The lead is the claim; the rest is the
 * qualification, and a reader who stops after the bold still leaves with the
 * point.
 */
export const Lead = ({ label, children, className = "" }) => (
  <span className={`[text-wrap:pretty] ${className}`}>
    <strong className="font-bold">{label}</strong>
    {children ? <> {children}</> : null}
  </span>
);

/** The design's inline alert glyph — an amber dot with a bang, sized to sit on
 *  a text baseline rather than above it. */
export const AlertGlyph = ({ tone = "warning" }) => (
  <span
    className={`mr-1.5 inline-flex h-[13px] w-[13px] shrink-0 items-center justify-center rounded-full align-[-2px] font-manrope text-[9px] font-bold leading-none ${
      tone === "danger"
        ? "bg-state-danger-border text-state-danger-text"
        : "bg-state-warning-border text-state-warning-text"
    }`}
  >
    !
  </span>
);

/** label / value row used by Route and Invoice detail. Production renders
 *  `?? "-"` for a missing value; a missing value is greyed here so an admin
 *  can tell "not set" from "set to something". */
export const FieldRow = ({ label, value }) => {
  const missing = value == null || value === "";
  return (
    <div className="flex items-baseline gap-2 py-[1.5px]">
      <span className="w-[76px] flex-none font-manrope text-[9.5px] font-semibold text-ui-text-muted">
        {label}
      </span>
      <span
        className={`min-w-0 break-words font-manrope text-[10.5px] font-medium ${
          missing ? "text-ui-text-subtle" : "text-ui-text"
        }`}
      >
        {missing ? "-" : value}
      </span>
    </div>
  );
};

/** Table header cell. */
export const Th = ({ children, align = "left", className = "" }) => (
  <th
    className={`px-[9px] py-1 font-jakarta text-[9.5px] font-semibold uppercase tracking-[0.07em] text-ui-text-muted ${
      align === "right"
        ? "text-right"
        : align === "center"
          ? "text-center"
          : "text-left"
    } ${className}`}
  >
    {children}
  </th>
);

/**
 * An honest placeholder for a panel whose data the API does not return yet.
 * Renders the shape so the design is legible, and says plainly that there is
 * nothing behind it. It must never show a number — an admin deciding whether
 * to re-bill off a fabricated variance is the worst outcome in this screen.
 */
export const NotBacked = ({ children }) => (
  <div className="px-2.5 py-4 text-center font-manrope text-[11px] font-medium leading-relaxed text-ui-text-muted [text-wrap:pretty]">
    {children}
  </div>
);

export default Panel;
